<script setup>
import { ref, computed, watch, watchEffect, onMounted, onUnmounted } from 'vue'
import { Button } from '@/components/ui/button'
import { X, Maximize2, Minimize2, ChevronLeft } from 'lucide-vue-next'
import { useWindowSize } from '@vueuse/core'
import { useSidebarPanelWidth } from '@/composables/useSidebarPanelWidth'

const windowSize = useWindowSize()

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'small',
    validator: (v) => ['small', 'medium', 'full'].includes(v)
  },
  showToggleButton: {
    type: Boolean,
    default: false
  },
  isToggled: {
    type: Boolean,
    default: false
  },
  show: {
    type: Boolean,
    default: true
  },
  showClose: {
    type: Boolean,
    default: true
  },
  showBack: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['toggle-full-width', 'close', 'back'])

const HANDLE_WIDTH = 14
const NAV_WIDTH = 74
const COLLAPSE_WIDTH = 40

const isTabletViewport = computed(() => windowSize.width.value >= 640)
const tabletMaxWidth = computed(() => windowSize.width.value - NAV_WIDTH)

const clampWidth = (px) => Math.max(HANDLE_WIDTH, Math.min(tabletMaxWidth.value, Math.round(px)))

const panelMaxWidthPx = computed(() => {
  const vw = windowSize.width.value
  if (props.size === 'full') return tabletMaxWidth.value
  // Wide detail content (members, settings, templates) must not get narrower
  // than it was on 13-15" laptops, so 720px is a floor rather than a cap.
  if (props.size === 'medium') return clampWidth(Math.max(720, vw * 0.45))
  return clampWidth(Math.max(320, vw * 0.3))
})

const storageKey = computed(() => `sidebarPanelWidth:${props.size}`)

const loadWidth = () => {
  if (props.size === 'full') return panelMaxWidthPx.value
  const stored = Number(localStorage.getItem(storageKey.value))
  return stored > 0 ? clampWidth(stored) : panelMaxWidthPx.value
}

const isShow = ref(props.show)
const drawerHeight = ref(60)
const drawerWidthPx = ref(loadWidth())
const isDragging = ref(false)
const headerHeight = ref(0)
const headerEl = ref(null)
let grabOffset = 0
let startY = 0
let dragRaf = 0
let pendingX = 0

const cancelDragFrame = () => {
  if (dragRaf) { cancelAnimationFrame(dragRaf); dragRaf = 0 }
}

const collapsed = computed(() => isTabletViewport.value && drawerWidthPx.value < COLLAPSE_WIDTH)

const publishedWidth = useSidebarPanelWidth()

watchEffect(() => {
  // Width of the visible card, not the panel: the handle strip beside it is
  // transparent, so overlays should clear the card and ignore the rail. Collapsed
  // to the bare rail counts as no panel at all.
  publishedWidth.value = isShow.value && isTabletViewport.value && !collapsed.value
    ? Math.max(0, drawerWidthPx.value - HANDLE_WIDTH)
    : 0
})

watch(() => props.show, (newVal) => {
  isShow.value = newVal
})

watch(() => props.size, () => {
  drawerWidthPx.value = loadWidth()
})

watch(tabletMaxWidth, () => {
  drawerWidthPx.value = props.size === 'full' ? tabletMaxWidth.value : clampWidth(drawerWidthPx.value)
})

onMounted(() => {
  if (headerEl.value) {
    headerHeight.value = headerEl.value.offsetHeight
  }
})

const startDrag = (e) => {
  if (!isTabletViewport.value && e.target.closest('button')) return
  isDragging.value = true
  e.currentTarget.setPointerCapture(e.pointerId)
  document.body.style.userSelect = 'none'
  if (isTabletViewport.value) {
    grabOffset = drawerWidthPx.value - (e.clientX - NAV_WIDTH)
  } else {
    startY = e.clientY
  }
}

const onDrag = (e) => {
  if (!isDragging.value) return

  if (isTabletViewport.value) {
    // One width commit per frame: the published width drives the map canvas
    // geometry, so an unbatched pointermove would relayout + resize every
    // Mapbox tile several times within a single frame.
    pendingX = e.clientX
    if (dragRaf) return
    dragRaf = requestAnimationFrame(() => {
      dragRaf = 0
      drawerWidthPx.value = clampWidth(pendingX - NAV_WIDTH + grabOffset)
    })
    return
  }

  const diff = startY - e.clientY
  const newHeight = drawerHeight.value + (diff / window.innerHeight) * 100

  const minHeight = Math.max(15, (headerHeight.value / window.innerHeight) * 100 + 5)
  const maxHeight = 90

  if (newHeight <= maxHeight && newHeight >= minHeight) {
    drawerHeight.value = newHeight
  }
  startY = e.clientY
}

const endDrag = (e) => {
  if (!isDragging.value) return
  isDragging.value = false
  e.currentTarget.releasePointerCapture?.(e.pointerId)
  document.body.style.userSelect = ''

  if (isTabletViewport.value) {
    if (dragRaf) {
      // Flush the frame that was still queued so we persist where the pointer
      // actually stopped, not where the last committed frame landed.
      cancelDragFrame()
      drawerWidthPx.value = clampWidth(pendingX - NAV_WIDTH + grabOffset)
    }
    if (props.size !== 'full') {
      localStorage.setItem(storageKey.value, String(drawerWidthPx.value))
    }
    return
  }

  const minHeight = Math.max(20, (headerHeight.value / window.innerHeight) * 100 + 10)
  const maxHeight = 90

  if (drawerHeight.value < minHeight) {
    drawerHeight.value = minHeight
  } else if (drawerHeight.value > maxHeight) {
    drawerHeight.value = maxHeight
  }
}

onUnmounted(() => {
  isDragging.value = false
  cancelDragFrame()
  document.body.style.userSelect = ''
})

const handleClose = () => {
  isShow.value = false
  emit('close')
}

const handleBack = () => {
  emit('back')
}
</script>

<template>
  <!-- Mobile bottom drawer / left drawer everywhere else -->
  <div
    v-if="isShow"
    class="fixed z-40 flex pointer-events-auto"
    :class="isTabletViewport
      ? 'top-0 bottom-0 left-[74px]'
      : 'bottom-14 left-0 right-0 flex-col bg-background shadow-lg rounded-t-xl overflow-hidden'"
    :style="isTabletViewport
      ? { width: `${Math.max(drawerWidthPx, HANDLE_WIDTH)}px` }
      : { height: `${Math.min(drawerHeight, 90)}vh`, maxHeight: '90vh' }"
  >
    <div
      v-show="!collapsed"
      class="flex flex-col flex-1 min-h-0 min-w-0"
      :class="isTabletViewport ? 'bg-background border-r border-border rounded-r-3xl overflow-hidden' : ''"
    >
      <div
        ref="headerEl"
        class="sidebar-panel-header border-b border-border z-10 flex-shrink-0"
        :class="{ 'cursor-move touch-none': !isTabletViewport }"
        @pointerdown="!isTabletViewport ? startDrag($event) : null"
        @pointermove="!isTabletViewport ? onDrag($event) : null"
        @pointerup="!isTabletViewport ? endDrag($event) : null"
        @pointercancel="!isTabletViewport ? endDrag($event) : null"
      >
        <div v-if="!isTabletViewport" class="flex justify-center pt-1.5">
          <div class="w-10 h-1 bg-muted-foreground/40 rounded-full"></div>
        </div>
        <div class="grid items-center px-2 py-2.5" style="grid-template-columns: 1fr auto 1fr">
          <div class="flex items-center gap-1">
            <Button
              v-if="showBack"
              @click.stop="handleBack"
              variant="secondary"
              class="w-8 h-8 p-0 rounded-full"
              title="Back"
            >
              <ChevronLeft class="w-5 h-5" />
            </Button>
            <slot name="leading" />
          </div>
          <div class="min-w-0 px-2 flex items-center justify-center gap-2">
            <slot name="title-leading" />
            <div class="min-w-0 text-center">
              <div class="font-bold truncate">{{ title }}</div>
              <div v-if="subtitle" class="text-xs text-muted-foreground truncate" :title="subtitle">
                {{ subtitle }}
              </div>
            </div>
          </div>
          <div class="flex items-center justify-end gap-1">
            <slot name="actions" />
            <Button
              v-if="showToggleButton"
              @click.stop="emit('toggle-full-width')"
              variant="ghost"
              class="w-6 h-6 p-0 rounded-full"
              :title="isToggled ? 'Exit full width' : 'Expand to full width'"
            >
              <Minimize2 v-if="isToggled" class="w-4 h-4 text-muted-foreground" />
              <Maximize2 v-else class="w-4 h-4 text-muted-foreground" />
            </Button>
            <Button
              v-if="size !== 'full' && showClose"
              @click="handleClose"
              variant="ghost"
              class="w-6 h-6 p-0 rounded-full"
              title="Close"
            >
              <X class="w-5 h-5 text-muted-foreground" />
            </Button>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        <slot />
      </div>
    </div>

    <div
      v-if="isTabletViewport"
      class="group w-3.5 shrink-0 flex items-center justify-center cursor-col-resize touch-none select-none"
      :class="collapsed ? 'bg-background/90 backdrop-blur shadow-lg rounded-r-xl' : ''"
      @pointerdown="startDrag"
      @pointermove="onDrag"
      @pointerup="endDrag"
      @pointercancel="endDrag"
    >
      <div
        class="w-2 h-28 rounded-full bg-muted-foreground/40 shadow-md transition-colors group-hover:bg-primary/80"
      ></div>
    </div>
  </div>
</template>
